// frontend/src/components/GoalsView.jsx
import React, { useEffect, useMemo, useState } from "react";
import { BACKEND_URL } from "../config";

function MilestonesBlock({ goal, onAdd, onToggle, onDelete, onUpdate, onAIGenerate }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [targetValue, setTargetValue] = useState("");
  const [targetUnit, setTargetUnit] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [aiCount, setAiCount] = useState(5);
  const [aiLoading, setAiLoading] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({
    title: "",
    description: "",
    target_value: "",
    target_unit: "",
    due_date: "",
  });

  const milestones = Array.isArray(goal?.milestones) ? goal.milestones : [];

  const resetAddForm = () => {
    setTitle("");
    setDescription("");
    setTargetValue("");
    setTargetUnit("");
    setDueDate("");
  };

  const add = () => {
    const t = title.trim();
    if (!t) return;

    onAdd?.(goal.goal_id, {
      title: t,
      description: description.trim() || null,
      target_value: targetValue !== "" ? Number(targetValue) : null,
      target_unit: targetUnit.trim() || null,
      due_date: dueDate || null,
    });

    resetAddForm();
  };

  const beginEdit = (milestone) => {
    setEditingId(milestone.milestone_id);
    setEditDraft({
      title: milestone.title || "",
      description: milestone.description || "",
      target_value: milestone.target_value ?? "",
      target_unit: milestone.target_unit ?? "",
      due_date: milestone.due_date ?? "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({
      title: "",
      description: "",
      target_value: "",
      target_unit: "",
      due_date: "",
    });
  };

  const saveEdit = async () => {
    if (!editingId || !editDraft.title.trim()) return;

    await onUpdate?.(goal.goal_id, editingId, {
      title: editDraft.title.trim(),
      description: editDraft.description.trim() || null,
      target_value: editDraft.target_value !== "" ? Number(editDraft.target_value) : null,
      target_unit: editDraft.target_unit.trim() || null,
      due_date: editDraft.due_date || null,
    });

    cancelEdit();
  };

  const runAI = async () => {
    setAiLoading(true);
    try {
      const suggestions = await onAIGenerate?.(goal, aiCount || 5);
      if (!suggestions?.length) return;

      const ok = window.confirm(`Add ${suggestions.length} AI milestones to this goal?`);
      if (!ok) return;

      for (const ms of suggestions) {
        // eslint-disable-next-line no-await-in-loop
        await onAdd?.(goal.goal_id, ms);
      }
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div style={{ display: "grid", gap: 10 }}>
        <input
          className="form-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Milestone title"
        />
        <input
          className="form-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
        />
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" }}>
          <input
            className="form-input"
            type="number"
            value={targetValue}
            onChange={(e) => setTargetValue(e.target.value)}
            placeholder="Target value (optional)"
          />
          <input
            className="form-input"
            value={targetUnit}
            onChange={(e) => setTargetUnit(e.target.value)}
            placeholder="Unit (optional)"
          />
        </div>
        <input
          className="form-input"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button type="button" className="btn-secondary" onClick={add} disabled={!title.trim()}>
            + Add milestone
          </button>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <input
              className="form-input"
              style={{ width: 90 }}
              type="number"
              min={3}
              max={10}
              value={aiCount}
              onChange={(e) => setAiCount(Number(e.target.value))}
            />
            <button type="button" className="btn-secondary" onClick={runAI} disabled={aiLoading}>
              {aiLoading ? "Generating…" : "AI milestones"}
            </button>
          </div>
        </div>
      </div>

      {milestones.length ? (
        <div style={{ display: "grid", gap: 8 }}>
          {milestones.map((milestone) => {
            const isEditing = editingId === milestone.milestone_id;

            return (
              <div
                key={milestone.milestone_id}
                style={{
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 12,
                  padding: 12,
                  display: "grid",
                  gap: 10,
                  background: "rgba(0,0,0,0.02)",
                }}
              >
                {isEditing ? (
                  <>
                    <input
                      className="form-input"
                      value={editDraft.title}
                      onChange={(e) => setEditDraft((draft) => ({ ...draft, title: e.target.value }))}
                      placeholder="Milestone title"
                    />
                    <input
                      className="form-input"
                      value={editDraft.description}
                      onChange={(e) => setEditDraft((draft) => ({ ...draft, description: e.target.value }))}
                      placeholder="Description (optional)"
                    />
                    <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" }}>
                      <input
                        className="form-input"
                        type="number"
                        value={editDraft.target_value}
                        onChange={(e) => setEditDraft((draft) => ({ ...draft, target_value: e.target.value }))}
                        placeholder="Target value (optional)"
                      />
                      <input
                        className="form-input"
                        value={editDraft.target_unit}
                        onChange={(e) => setEditDraft((draft) => ({ ...draft, target_unit: e.target.value }))}
                        placeholder="Unit (optional)"
                      />
                    </div>
                    <input
                      className="form-input"
                      type="date"
                      value={editDraft.due_date || ""}
                      onChange={(e) => setEditDraft((draft) => ({ ...draft, due_date: e.target.value }))}
                    />
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button type="button" className="btn-secondary" onClick={cancelEdit}>
                        Cancel
                      </button>
                      <button type="button" className="btn-primary" onClick={saveEdit} disabled={!editDraft.title.trim()}>
                        Save milestone
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", minWidth: 0 }}>
                      <input
                        type="checkbox"
                        checked={!!milestone.completed}
                        onChange={(e) => onToggle?.(goal.goal_id, milestone.milestone_id, e.target.checked)}
                        style={{ marginTop: 3 }}
                      />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 800, textDecoration: milestone.completed ? "line-through" : "none" }}>
                          {milestone.title}
                        </div>
                        <div className="body-small" style={{ color: "var(--text-secondary)" }}>
                          {milestone.description ? milestone.description : ""}
                          {milestone.target_value != null
                            ? ` • Target: ${milestone.target_value}${milestone.target_unit ? ` ${milestone.target_unit}` : ""}`
                            : ""}
                          {milestone.due_date ? ` • Due: ${milestone.due_date}` : ""}
                          {milestone.completed_at ? ` • Completed: ${new Date(milestone.completed_at).toLocaleDateString()}` : ""}
                        </div>
                      </div>
                    </label>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button type="button" className="btn-secondary" onClick={() => beginEdit(milestone)}>
                        Edit milestone
                      </button>
                      <button type="button" className="btn-secondary" onClick={() => onDelete?.(goal.goal_id, milestone.milestone_id)}>
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="body-small" style={{ color: "var(--text-secondary)" }}>
          No milestones yet.
        </div>
      )}
    </div>
  );
}

function GoalsView({
  goals,
  userId,
  isAdmin,
  onCreateGoal,
  onCompleteGoal,
  onDeleteGoal,
  token,
  onOpenGoalModal,
  onCloseGoalModal,
  showToast,
}) {
  const [progressMetrics, setProgressMetrics] = useState({
    weight: null,
    bf: null,
    muscle: null,
    systolic: null,
    diastolic: null,
  });
  const [metricsLoading, setMetricsLoading] = useState(false);

  const [expandedGoal, setExpandedGoal] = useState(null);
  const [scope, setScope] = useState("self");
  const [people, setPeople] = useState([]);
  const [viewFilter, setViewFilter] = useState("me");
  const [filteredGoals, setFilteredGoals] = useState(goals || []);
  const [loadingGoals, setLoadingGoals] = useState(false);

  const [editingGoalId, setEditingGoalId] = useState(null);
  const [editDraft, setEditDraft] = useState({
    title: "",
    description: "",
    target_value: "",
    target_unit: "",
    due_date: "",
  });

  const hasAutoGoals = useMemo(() => {
    return (goals || []).some((goal) => {
      const source = String(goal?.created_by || goal?.source || goal?.origin || "").toLowerCase();
      return source.includes("ai") || source.includes("questionnaire") || source.includes("autogen");
    });
  }, [goals]);

  const normalizePeople = useMemo(() => {
    return (people || [])
      .filter((person) => person && person.user_id)
      .map((person) => ({
        user_id: person.user_id,
        name: person.name || person.email || person.user_id,
        email: person.email || "",
        role: person.role || "",
      }));
  }, [people]);

  const canSeeAll = scope === "admin";
  const canManageOthers = scope === "admin" || scope === "trainer";

  const loadProgressMetrics = async () => {
    if (!token) return;

    setMetricsLoading(true);
    try {
      const fetchMetric = async (metricKey) => {
        const qs = new URLSearchParams();
        qs.set("metric", metricKey);
        qs.set("days", "90");
        qs.set("user_id", "me");
        const res = await fetch(`${BACKEND_URL}/api/metrics/timeseries?${qs.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return null;
        const data = await res.json();
        const latest = data?.aggregate?.latest ?? null;
        return latest == null ? null : Number(latest);
      };

      const [weight, bf, muscle, sys, dia] = await Promise.all([
        fetchMetric("app.weight"),
        fetchMetric("withings.fat_ratio_percent"),
        fetchMetric("withings.muscle_mass_lbs"),
        fetchMetric("withings.systolic"),
        fetchMetric("withings.diastolic"),
      ]);

      setProgressMetrics({
        weight,
        bf,
        muscle,
        systolic: sys,
        diastolic: dia,
      });
    } catch (e) {
      console.warn("Failed to load progress metrics:", e?.message || e);
    } finally {
      setMetricsLoading(false);
    }
  };

  useEffect(() => {
    loadProgressMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    return () => {
      onCloseGoalModal?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadGoalScope = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/goals/scope`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;

      const data = await res.json();
      const nextScope = data.scope || (isAdmin ? "admin" : "self");
      setScope(nextScope);
      setPeople(data.users || []);
      setViewFilter("me");
    } catch (e) {
      setScope(isAdmin ? "admin" : "self");
      setViewFilter(isAdmin ? "all" : "me");
    }
  };

  const loadGoalsForFilter = async (filterValue) => {
    if (!canManageOthers) {
      setFilteredGoals(goals || []);
      return;
    }

    setLoadingGoals(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/goals/list?user_id=${encodeURIComponent(filterValue)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setFilteredGoals(data.goals || []);
      } else if (filterValue !== "all" && filterValue !== "me") {
        const res2 = await fetch(`${BACKEND_URL}/api/goals/users/${encodeURIComponent(filterValue)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res2.ok) {
          const data2 = await res2.json();
          setFilteredGoals(data2.goals || []);
        }
      }
    } catch (e) {
      console.error("Error loading goals:", e);
    } finally {
      setLoadingGoals(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadGoalScope();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!canManageOthers) {
      setFilteredGoals(goals || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goals, canManageOthers]);

  useEffect(() => {
    if (!token) return;
    if (canManageOthers) {
      loadGoalsForFilter(viewFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, viewFilter, scope]);

  const refreshGoals = async () => {
    if (canManageOthers) {
      await loadGoalsForFilter(viewFilter);
    } else {
      setFilteredGoals(Array.isArray(goals) ? [...goals] : []);
    }
  };

  useEffect(() => {
    const handler = async () => {
      try {
        await refreshGoals();
        await loadProgressMetrics();
      } catch (e) {
        // ignore
      }
    };

    window.addEventListener("goals:refresh", handler);
    return () => window.removeEventListener("goals:refresh", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, viewFilter, scope, goals]);

  const openEditGoal = (goal) => {
    setEditingGoalId(goal.goal_id);
    setEditDraft({
      title: goal.title || "",
      description: goal.description || "",
      target_value: goal.target_value ?? "",
      target_unit: goal.target_unit ?? "",
      due_date: goal.due_date ?? "",
    });
  };

  const cancelEditGoal = () => {
    setEditingGoalId(null);
  };

  const saveEditGoal = async (goalId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/goals/${encodeURIComponent(goalId)}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: editDraft.title.trim(),
          description: editDraft.description.trim() || null,
          target_value: editDraft.target_value !== "" ? Number(editDraft.target_value) : null,
          target_unit: editDraft.target_unit.trim() || null,
          due_date: editDraft.due_date || null,
        }),
      });

      if (!res.ok) throw new Error(await res.text());
      setEditingGoalId(null);
      await refreshGoals();
    } catch (e) {
      showToast?.("Error updating goal: " + (e?.message || String(e)), "error");
    }
  };

  const addMilestone = async (goalId, milestone) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/goals/${encodeURIComponent(goalId)}/milestones`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(milestone),
      });

      if (!res.ok) throw new Error(await res.text());
      await refreshGoals();
    } catch (e) {
      showToast?.("Error adding milestone: " + (e?.message || String(e)), "error");
    }
  };

  const toggleMilestone = async (goalId, milestoneId, completed) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/goals/${encodeURIComponent(goalId)}/milestones/${encodeURIComponent(milestoneId)}/complete`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ completed: !!completed }),
      });

      if (!res.ok) throw new Error(await res.text());
      await refreshGoals();
    } catch (e) {
      showToast?.("Error updating milestone: " + (e?.message || String(e)), "error");
    }
  };

  const updateMilestone = async (goalId, milestoneId, milestone) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/goals/${encodeURIComponent(goalId)}/milestones/${encodeURIComponent(milestoneId)}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(milestone),
      });

      if (!res.ok) throw new Error(await res.text());
      await refreshGoals();
    } catch (e) {
      showToast?.("Error saving milestone: " + (e?.message || String(e)), "error");
    }
  };

  const deleteMilestone = async (goalId, milestoneId) => {
    if (!window.confirm("Delete this milestone?")) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/goals/${encodeURIComponent(goalId)}/milestones/${encodeURIComponent(milestoneId)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error(await res.text());
      await refreshGoals();
    } catch (e) {
      showToast?.("Error deleting milestone: " + (e?.message || String(e)), "error");
    }
  };

  const generateMilestonesAI = async (goal, count = 5) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/goals/ai/milestones`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: goal.user_id || "me",
          goal_title: goal.title,
          goal_description: goal.description || null,
          target_value: goal.target_value ?? null,
          target_unit: goal.target_unit ?? null,
          due_date: goal.due_date ?? null,
          milestone_count: count,
        }),
      });

      if (!res.ok) throw new Error(await res.text());
      const out = await res.json();
      return out.milestones || [];
    } catch (e) {
      showToast?.("Error generating milestones: " + (e?.message || String(e)), "error");
      return [];
    }
  };

  const activeGoals = (filteredGoals || []).filter((goal) => !goal.completed);
  const completedGoals = (filteredGoals || []).filter((goal) => goal.completed);

  const GoalCard = ({ goal, isCompleted = false }) => {
    const milestoneCount = Array.isArray(goal?.milestones) ? goal.milestones.length : 0;
    const doneMilestoneCount = Array.isArray(goal?.milestones)
      ? goal.milestones.filter((milestone) => !!milestone?.completed).length
      : 0;

    return (
      <div className={`goal-card ${isCompleted ? "completed" : ""}`} data-testid={`goal-${goal.goal_id}`}>
        <div className="goal-header">
          <h4 className="heading-4">{goal.title}</h4>
          {goal.assigned_by && !isCompleted && <span className="goal-badge">Assigned by {goal.assigned_by_name}</span>}
          {isCompleted && <span className="goal-badge success">✓ Completed</span>}
        </div>

        {goal.description && <p className="body-small" style={{ margin: "8px 0" }}>{goal.description}</p>}

        {goal.target_value != null && goal.target_unit && (
          <p className="body-small" style={{ fontWeight: 600 }}>
            Target: {goal.target_value} {goal.target_unit}
          </p>
        )}

        {goal.metric_rule && goal.metric_rule.domain && goal.metric_rule.field ? (
          <p className="body-small" style={{ color: "var(--text-secondary)" }}>
            Metric rule: {goal.metric_rule.domain}.{goal.metric_rule.field} {goal.metric_rule.operator} {goal.metric_rule.value}
            {goal.metric_actual != null ? ` (actual: ${goal.metric_actual})` : ""}
            {goal.metric_met === true ? " ✓" : goal.metric_met === false ? " ✕" : ""}
          </p>
        ) : null}

        {goal.due_date && !isCompleted && (
          <p className="body-small" style={{ color: "var(--text-secondary)" }}>
            Due: {new Date(goal.due_date).toLocaleDateString()}
          </p>
        )}

        {isCompleted && goal.completed_at && (
          <p className="body-small" style={{ color: "var(--text-secondary)" }}>
            Completed: {new Date(goal.completed_at).toLocaleDateString()}
          </p>
        )}

        {!isCompleted && editingGoalId === goal.goal_id && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              border: "1px solid var(--border-light)",
              borderRadius: 10,
              background: "var(--bg-secondary)",
            }}
          >
            <div className="form-group">
              <label className="body-small">Title</label>
              <input
                className="form-input"
                value={editDraft.title}
                onChange={(e) => setEditDraft((draft) => ({ ...draft, title: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label className="body-small">Description</label>
              <textarea
                className="form-input"
                rows={3}
                value={editDraft.description}
                onChange={(e) => setEditDraft((draft) => ({ ...draft, description: e.target.value }))}
              />
            </div>

            <div className="form-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="form-group">
                <label className="body-small">Target value</label>
                <input
                  type="number"
                  className="form-input"
                  value={editDraft.target_value}
                  onChange={(e) => setEditDraft((draft) => ({ ...draft, target_value: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="body-small">Target unit</label>
                <input
                  className="form-input"
                  value={editDraft.target_unit}
                  onChange={(e) => setEditDraft((draft) => ({ ...draft, target_unit: e.target.value }))}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="body-small">Due date</label>
              <input
                type="date"
                className="form-input"
                value={editDraft.due_date || ""}
                onChange={(e) => setEditDraft((draft) => ({ ...draft, due_date: e.target.value }))}
              />
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
              <button type="button" className="btn-secondary" onClick={cancelEditGoal}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => saveEditGoal(goal.goal_id)}
                disabled={!editDraft.title.trim()}
              >
                Save
              </button>
            </div>
          </div>
        )}

        <div className="goal-actions" style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {!isCompleted && (
            <button
              onClick={async () => {
                await onCompleteGoal(goal.goal_id);
                await refreshGoals();
              }}
              className="btn-primary"
              data-testid={`complete-goal-${goal.goal_id}`}
            >
              Mark Complete
            </button>
          )}

          {!isCompleted && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => openEditGoal(goal)}
              data-testid={`edit-goal-${goal.goal_id}`}
            >
              Edit
            </button>
          )}

          <button
            onClick={() => setExpandedGoal((current) => (current === goal.goal_id ? null : goal.goal_id))}
            className="btn-secondary"
            data-testid={`toggle-comments-${goal.goal_id}`}
          >
            {expandedGoal === goal.goal_id
              ? "Hide Milestones"
              : `View Milestones${milestoneCount ? ` (${doneMilestoneCount}/${milestoneCount})` : ""}`}
          </button>

          <button
            onClick={async () => {
              await onDeleteGoal(goal.goal_id);
              await refreshGoals();
            }}
            className="btn-secondary"
            style={{ color: "#dc2626", borderColor: "#dc2626" }}
            data-testid={`delete-goal-${goal.goal_id}`}
          >
            Delete
          </button>
        </div>

        {expandedGoal === goal.goal_id && (
          <div
            style={{
              marginTop: 16,
              padding: 16,
              background: "var(--bg-secondary)",
              borderRadius: 8,
              border: "1px solid var(--border-light)",
            }}
          >
            <h5 className="body-medium" style={{ fontWeight: 600, marginBottom: 12 }}>
              Milestones
            </h5>

            <MilestonesBlock
              goal={goal}
              onAdd={addMilestone}
              onToggle={toggleMilestone}
              onDelete={deleteMilestone}
              onUpdate={updateMilestone}
              onAIGenerate={generateMilestonesAI}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="goals-view">
      <div className="goals-header">
        <h1 className="heading-2 goals-title">Goals</h1>

        <button
          type="button"
          onClick={() => onOpenGoalModal?.()}
          className="btn-primary goals-assign-btn"
          data-testid="create-goal-btn"
        >
          {canManageOthers ? "Assign Goal" : "Create Goal"}
        </button>
      </div>

      

      {canManageOthers && (
        <div className="form-group" style={{ maxWidth: 520, marginBottom: 16 }}>
          <label className="body-medium">View goals for</label>
          <select className="form-input" value={viewFilter} onChange={(e) => setViewFilter(e.target.value)}>
            {canSeeAll && <option value="all">All goals</option>}
            <option value="me">My goals</option>
            {normalizePeople.map((person) => (
              <option key={person.user_id} value={person.user_id}>
                {person.name} {person.email ? `(${person.email})` : ""} {person.role ? `— ${person.role}` : ""}
              </option>
            ))}
          </select>
          {loadingGoals && (
            <div className="body-small" style={{ marginTop: 8, color: "var(--text-muted)" }}>
              Loading…
            </div>
          )}
        </div>
      )}

      <div className="goals-sections">
        <div className="goals-section">
          <h3 className="heading-3">Active Goals ({activeGoals.length})</h3>
          {activeGoals.length === 0 ? (
            <p className="body-medium" style={{ color: "var(--text-muted)", fontStyle: "italic" }}>
              No active goals
            </p>
          ) : (
            <div className="goals-list">{activeGoals.map((goal) => <GoalCard key={goal.goal_id} goal={goal} />)}</div>
          )}
        </div>
        

        <div className="goals-section" style={{ marginTop: 32 }}>
          <h3 className="heading-3">Completed Goals ({completedGoals.length})</h3>
          {completedGoals.length === 0 ? (
            <p className="body-medium" style={{ color: "var(--text-muted)", fontStyle: "italic" }}>
              No completed goals yet
            </p>
          ) : (
            <div className="goals-list">{completedGoals.map((goal) => <GoalCard key={goal.goal_id} goal={goal} isCompleted />)}</div>
          )}
        </div>

        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div>
            <div className="heading-4" style={{ marginBottom: 4 }}>Progress Metrics</div>
            <div className="body-small" style={{ color: "var(--text-muted)" }}>
              {hasAutoGoals
                ? "These goals were created from your questionnaire and are tracked automatically where possible."
                : "Your goals and key health metrics tracked over time."}
            </div>
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={loadProgressMetrics}
            disabled={metricsLoading}
            style={{ padding: "8px 12px" }}
          >
            {metricsLoading ? "…" : "Refresh"}
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
            marginTop: 12,
          }}
        >
          <div className="card" style={{ padding: 14, textAlign: "center" }}>
            <div className="body-small" style={{ color: "var(--text-muted)" }}>Weight</div>
            <div className="heading-3" style={{ margin: 0 }}>
              {progressMetrics.weight == null ? "—" : progressMetrics.weight.toFixed(1)}
            </div>
            <div className="body-small" style={{ color: "var(--text-secondary)" }}>lbs</div>
          </div>

          <div className="card" style={{ padding: 14, textAlign: "center" }}>
            <div className="body-small" style={{ color: "var(--text-muted)" }}>Body Fat</div>
            <div className="heading-3" style={{ margin: 0 }}>
              {progressMetrics.bf == null ? "—" : `${progressMetrics.bf.toFixed(1)}%`}
            </div>
            <div className="body-small" style={{ color: "var(--text-secondary)" }}>Withings</div>
          </div>

          <div className="card" style={{ padding: 14, textAlign: "center" }}>
            <div className="body-small" style={{ color: "var(--text-muted)" }}>Muscle Mass</div>
            <div className="heading-3" style={{ margin: 0 }}>
              {progressMetrics.muscle == null ? "—" : progressMetrics.muscle.toFixed(1)}
            </div>
            <div className="body-small" style={{ color: "var(--text-secondary)" }}>lbs (Withings)</div>
          </div>

          <div className="card" style={{ padding: 14, textAlign: "center" }}>
            <div className="body-small" style={{ color: "var(--text-muted)" }}>Blood Pressure</div>
            <div className="heading-3" style={{ margin: 0 }}>
              {progressMetrics.systolic == null && progressMetrics.diastolic == null
                ? "—"
                : `${progressMetrics.systolic ?? "—"}/${progressMetrics.diastolic ?? "—"}`}
            </div>
            <div className="body-small" style={{ color: "var(--text-secondary)" }}>mmHg</div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default GoalsView;
